import { useEffect, useState } from 'react';

import type {
  BroadcastContent,
  ConnectingLineDelayByStationProperties,
  ConnectingLineEntry,
} from '../../../api/types';
import type { IStationsLine } from '../../../data/istationsLines';
import type { RouteMaster } from '../../../data/routes';
import { getRouteColor } from '../../../data/routes';

import { sortStopsBySequence, type RouteStop } from '../../../data/routeStops';
import {
  PHRASE_TEMPLATE_CONNECTING_STATION_INTRO,
  PHRASE_TEMPLATE_CONNECTING_STATION_OVER_LIMIT,
} from '../../../data/phraseTemplates';
import { usePhraseTemplateDefaults } from '../../../contexts/PhraseTemplateContext';
import {
  cloneConnectingStationLine,
  CONNECTING_STATION_INTRO_DEFAULT_SSML,
  CONNECTING_STATION_OVER_LIMIT_DEFAULT_SSML,
  createDefaultConnectingStationLine,
  isOverLimitNotEffective,
  isReplaceOverLinesNotEffective,
  overLimitBehaviorFromLimits,
  resolveEffectiveSsml,
  resolveLocalizedDefaults,
  usesOverLimitPhrase,
} from '../../../utils/broadcastContents';
import type { RevealTarget, ValidationIssue } from '../../../utils/validationIssues';
import { hasIssueForTestId } from '../../../utils/validationIssues';

import { AudioPreviewField } from '../parts/AudioPreviewField';
import { StopSelect } from '../parts/StopSelect';
import { deriveContentSampleOverrides, resolveContentPlaceholders } from '../previewSample';
import type { PreviewSsmlHandler } from '../types';
import { ConnectingLineBlock } from './ConnectingLineBlock';
import { LocalizedTextFields } from '../parts/LocalizedTextFields';
import { LocalizedRouteNicknameFields } from '../parts/LocalizedRouteNicknameFields';
import { StatusBanner } from '../../StatusBanner';
import { HintTooltip } from '../../HintTooltip';
import {
  LOCALIZED_STATION_INTRO,
  LOCALIZED_STATION_OVER_LIMIT,
} from '../../../utils/localizedSsml';

/**
 * 放送路線数の制限を決めるプルダウン 2 つのラベル。
 * 「超えたら」を入れ、どちらが打ち切り・どちらが定型文への切り替えかがラベルだけで読めるようにする。
 */
const FIRST_LIMIT_LABEL = '何路線まで遅延放送するか';
const SECOND_LIMIT_LABEL = '遅延が何路線を超えたら定型文にするか';

// ===========================================================================
// 接続線遅延 (乗換駅ベース / part2 / `connecting_line_delay_by_station`) の UI
// ===========================================================================
//
// 三層構造:
//   ConnectingLineDelayByStationFields      … コンテンツ全体 (乗換駅+接続線一覧+冒頭SSML)
//     └── ConnectingLineBlock (接続線1本)   … route_id / 判定モード / 乗換時間 / 方面一覧 / 遅延放送パターン
//            └── ConnectingDirectionBlock   … direction_id / direction_name / 区間 (section時)
//
// 状態管理は上流 (BroadcastContentsEditor) がコントロールドに握るため、各コンポーネントは
// 「現在の値」と「更新関数」を props で受け取り、内部状態は保持しない。

/**
 * 接続線遅延 (乗換駅ベース / part2) のトップレベルフィールド。
 * 乗換駅、冒頭SSML、接続線ブロック一覧を編集する。
 */
export function ConnectingLineDelayByStationFields({
  props,
  content,
  contentIndex = 0,
  parentVoiceId,
  playbackRate,
  onPreviewSsml,
  routes,
  routeStops,
  istationsLines = [],
  parentRouteId,
  issues = [],
  revealTarget = null,
  onChange,
}: {
  props: Partial<ConnectingLineDelayByStationProperties>;
  /**
   * このコンテンツに紐づく入力エラー (親で `contentIndex` 絞り込み済み)。
   * 接続線タブの ⚠ マーク / 乗換駅・冒頭 SSML 欄の赤枠に使う。
   */
  issues?: ValidationIssue[];
  /** 接続線タブ (と配下の方面タブ) を自動で開くための指示。 */
  revealTarget?: RevealTarget | null;

  /**
   * プレビュー対象の放送コンテンツ (Issue #439)。
   * サーバ側でこの内容をレンダリングして実放送と同一の文面を合成する。
   * part2 は「冒頭文 + 接続線ブロック文 + 方面部分文」の三層をサーバが組み立てるため、
   * 通し (コンテンツ全体) のプレビューになる。
   */
  content?: BroadcastContent;
  /** `data-testid` を一意にするためのコンテンツ index (Issue #439) */
  contentIndex?: number;
  /** 親フォームで選択中の Polly VoiceId (実放送と同じ声色で試聴するため) */
  parentVoiceId?: string;
  /** 親コンテンツの `playback_rate` (プレビュー音声にも反映する) */
  playbackRate?: number;
  /** 音声プレビュー処理。省略時はプレビュー UI を表示しない */
  onPreviewSsml?: PreviewSsmlHandler;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  /**
   * 「運行情報の参照元（アイステイションズ路線）」の選択肢マスタ。
   * 各接続線ブロック (ConnectingLineBlock) にそのまま渡す。
   * ATOS 線区マスタ (`routes`) とは独立した別系統で、連動させない。
   */
  istationsLines?: IStationsLine[];

  /**
   * 親フォーム側で編集中の放送地点の「自路線」の route_id。
   * 乗換駅ドロップダウンの選択肢を、この route_id に対応する駅一覧のみに絞り込むために使う。
   * 詳細は `BroadcastContentsEditorProps.parentRouteId` を参照。
   */
  parentRouteId?: string;
  onChange: (props: ConnectingLineDelayByStationProperties) => void;
}): React.JSX.Element {
  const transferStopId = props.transfer_stop_id ?? '';
  /*
   * Issue #552: 冒頭 SSML の入力値は **未設定なら空欄のまま** にする。
   *
   * 以前はここでコード内既定文面 (`CONNECTING_STATION_INTRO_DEFAULT_SSML`) を
   * 値として流し込んでいたため、
   *   1. placeholder (薄字) が一度も表示されない (= マスタの文面を確認できない)
   *   2. `emit()` が既定文面を実値として保存してしまい、以後マスタ編集が反映されない
   * という Issue #527 の規約違反が残っていた。実際に放送される文面は
   * placeholder (定型文マスタ → コード内既定) で提示する。
   */
  const introTemplate = props.ssml_intro_template ?? '';
  const lines: ConnectingLineEntry[] = Array.isArray(props.lines) ? props.lines : [];

  /*
   * Issue #552: 定型文マスタ (`connecting_station_intro`) の既定文面。
   *
   * 「薄字 = 実際に放送される文面」を成立させるため、placeholder は
   * マスタ (日本語) → コード内既定 の順に解決する。
   */
  const resolveDefault = usePhraseTemplateDefaults();
  const effectiveIntroTemplate = resolveEffectiveSsml(
    props.ssml_intro_template,
    PHRASE_TEMPLATE_CONNECTING_STATION_INTRO,
    CONNECTING_STATION_INTRO_DEFAULT_SSML,
    resolveDefault,
  );

  /*
   * 上限超過時の案内文 (`over_limit_ssml_template`) の薄字。
   * 冒頭文と同じ解決順 (定型文マスタ → コード内既定) で、放送側の
   * 固定文の解決 (`connecting_station_over_limit`) と揃える。値としては保存しない。
   */
  const overLimitTemplate = props.over_limit_ssml_template ?? '';
  const effectiveOverLimitTemplate = resolveEffectiveSsml(
    props.over_limit_ssml_template,
    PHRASE_TEMPLATE_CONNECTING_STATION_OVER_LIMIT,
    CONNECTING_STATION_OVER_LIMIT_DEFAULT_SSML,
    resolveDefault,
  );
  const maxBroadcastLines = props.max_broadcast_lines;
  const hasOverLimit = typeof maxBroadcastLines === 'number';
  const overLimitBehavior = props.over_limit_behavior;
  const replaceOverLines = props.replace_over_lines;
  /** 案内文 (日本語 / 言語別) を入力できるのは、固定文を使う動作 (固定文のみ / 併用) のとき。 */
  const isReplaceWithPhrase = usesOverLimitPhrase(overLimitBehavior);
  /*
   * 2 つのプルダウンの値は保存形から導出する (動作は 2 つの組み合わせで決まる)。
   *   - 打ち切りのみ:  1 つめ = max_broadcast_lines
   *   - 固定文のみ:    2 つめ = max_broadcast_lines
   *   - 併用:          1 つめ = max_broadcast_lines / 2 つめ = replace_over_lines
   * 動作が無いのに max_broadcast_lines だけある旧データは 1 つめとして見せる (保存時の検証で止める)。
   */
  const isCombined = overLimitBehavior === 'truncate_then_replace';
  const firstValue: number | undefined =
    hasOverLimit && overLimitBehavior !== 'replace_with_phrase' ? maxBroadcastLines : undefined;
  const secondValue: number | undefined = isCombined
    ? replaceOverLines
    : overLimitBehavior === 'replace_with_phrase'
      ? maxBroadcastLines
      : undefined;

  // タブ表示: 選択中の接続線ブロック index。接続線が複数あるとき横並びタブで切り替える。
  const [activeLineIndex, setActiveLineIndex] = useState<number>(0);
  const activeIndex = Math.min(activeLineIndex, Math.max(0, lines.length - 1));

  /*
   * エラーサマリから「該当接続線を開く」指示が来たらタブを切り替える。
   *
   * 接続線はタブ表示で選択中の 1 件しか本文を描画しないため、
   * タブを切り替えないと該当入力欄が DOM に存在せずスクロールもできない。
   * `nonce` を依存配列に含めるのは「同じ接続線をもう一度開く」ケースのため
   * (詳細は `RevealTarget` の JSDoc)。
   */
  useEffect(() => {
    if (revealTarget?.lineIndex === undefined) return;
    setActiveLineIndex(revealTarget.lineIndex);
    // nonce は「同じ index でも再実行させる」ためだけに依存配列へ含める。
  }, [revealTarget?.lineIndex, revealTarget?.nonce]);

  /**
   * 指定した接続線にエラーがあるか (タブの ⚠ マーク判定)。
   *
   * `issues` は親で当コンテンツぶんに絞り込まれているので、`lineIndex` の一致だけを見る。
   * 「タブを開かないとエラーの有無すら分からない」状態を避けるのが目的。
   */
  const lineHasIssue = (lineIndex: number): boolean =>
    issues.some((i) => i.target.lineIndex === lineIndex);

  // 「乗換駅」ドロップダウンの選択肢は、親フォームで選択中の路線 (parentRouteId) の駅一覧を使う。
  //
  // 親から渡される `routeStops` は複数 route_id ぶんが 1 つの Record にまとまっている
  // (BroadcastPointsPage が全路線ぶん事前取得しているため) ので、明示的に
  // `routeStops[parentRouteId]` で自路線に絞り込む必要がある。
  //
  // 以前は `Object.values(routeStops)[0]` で「Record の先頭キー」を暗黙採用していたが、
  // Record の並び順は保証されず、複数 route_id が入っていると自路線とは無関係な路線
  // (例: 中総緩の地点を編集中なのに京浜東北線の駅が候補に出る) の駅一覧が
  // 表示される不具合が発生していたため、`parentRouteId` による明示指定に変更した。
  //
  // `parentRouteId` が未指定 / 該当する駅一覧が routeStops に無い場合は空配列とし、
  // 誤って別路線の駅が表示されないようにする (乗換駅ドロップダウンは
  // 「(指定なし)」のみとなる)。
  const parentStops = sortStopsBySequence(parentRouteId ? (routeStops[parentRouteId] ?? []) : []);

  /**
   * properties 全体を新しい値でまとめて置き換えるヘルパ
   *
   * Issue #552: 基底値に `ssml_intro_template` を **含めない**。
   * 以前はコード内既定文面を基底値に置いていたため、乗換駅を選び直すなど
   * 冒頭文と無関係な操作をしただけで既定文面が実値として焼き込まれ、
   * 以後そのコンテンツには定型文マスタが一切反映されなくなっていた。
   * 冒頭文はユーザーが実際に入力したときだけ `patch` 経由で書き込む
   * (未入力なら `props` にキーが無いまま = マスタ委譲)。
   */
  const emit = (patch: Partial<ConnectingLineDelayByStationProperties>): void => {
    const next: Record<string, unknown> = {
      transfer_stop_id: transferStopId,
      lines,
      ...props,
      ...patch,
    };
    // `patch` で `undefined` を渡したキーは「未設定」に戻す (キーごと落とす)。
    // 上限を「制限しない」へ戻したときに、値が undefined のキーが残らないようにするため。
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined) delete next[key];
    }
    onChange(next as unknown as ConnectingLineDelayByStationProperties);
  };

  /** 接続線一覧の index 番目を patch で更新する */

  const updateLine = (index: number, next: ConnectingLineEntry): void => {
    const nextLines = lines.slice();
    nextLines[index] = next;
    emit({ lines: nextLines });
  };

  /**
   * 接続線を index の直後に追加する。
   *
   * 追加した接続線 (= index + 1) をそのまま選択中タブにする。
   * 接続線ブロックはタブ表示で 1 件ぶんしか本文を描画しないため、追加直後に
   * 選択タブを切り替えないと「追加したのに画面が変わらない」状態になり、
   * ユーザが追加した接続線に気付けない (Issue #356)。
   */
  const addLineAfter = (index: number): void => {
    const nextLines = lines.slice();
    nextLines.splice(index + 1, 0, createDefaultConnectingStationLine());
    setActiveLineIndex(index + 1);
    emit({ lines: nextLines });
  };

  /**
   * 接続線を末尾に追加する (ヘッダーの「＋ 接続線を追加」ボタン用)。
   *
   * `addLineAfter` と同様、追加した末尾の接続線を選択中タブにして
   * 追加直後にその内容を編集できるようにする (Issue #356)。
   * 追加後の末尾 index は「追加前の件数」= `lines.length` となる。
   */
  const addLineToEnd = (): void => {
    setActiveLineIndex(lines.length);
    emit({ lines: [...lines, createDefaultConnectingStationLine()] });
  };

  /**
   * 接続線 `index` を **複製** して直後 (index + 1) に挿入する。
   *
   * 方面 / 遅延パターン / 各 SSML テンプレートを一式引き継ぐため、
   * 「1 方面だけ違う接続線」を作る際の入力し直しが不要になる
   * (受入条件「設定を他の接続線ブロックへコピーできること」)。
   *
   * ディープコピーは `cloneConnectingStationLine()` に委譲する。浅いコピーだと
   * `directions[]` / `delay_patterns[]` などが複製元と同じ参照になり、
   * 片方を編集すると両方変わる (= 気付きにくいデータ破壊) ため。
   *
   * `addLineAfter` と同様に複製先を選択中タブへ切り替える。接続線はタブ表示で
   * 1 件ぶんしか本文を描画しないので、切り替えないと「複製したのに画面が
   * 変わらない」状態になり、複製されたことに気付けない (Issue #356 と同じ配慮)。
   */
  const duplicateLineAfter = (index: number): void => {
    const source = lines[index];
    if (!source) return;
    const nextLines = lines.slice();
    nextLines.splice(index + 1, 0, cloneConnectingStationLine(source));
    setActiveLineIndex(index + 1);
    emit({ lines: nextLines });
  };

  /**
   * 2 つのプルダウン (何路線まで遅延放送するか / 遅延が何路線を超えたら定型文にするか) の値から、
   * 動作と本数の保存形を決めて書き込む。「制限しない」は `undefined`。
   *
   * - どちらも制限しない: 本数・動作・案内文をすべて消す。
   * - 1 つめだけ: 打ち切り (`truncate`)。本数は `max_broadcast_lines`。
   * - 2 つめだけ: 固定文のみ (`replace_with_phrase`)。本数は `max_broadcast_lines`。
   * - 両方: 併用 (`truncate_then_replace`)。本数は 1 つめが `max_broadcast_lines`、
   *   2 つめが `replace_over_lines`。
   * 本数の保存先が動作で変わるため、どちらかを変えるときも、もう一方の現在値を渡して保存先を決め直す。
   * 案内文は固定文を使う動作 (固定文のみ / 併用) の間だけ残す。
   */
  const applyLimits = (first: number | undefined, second: number | undefined): void => {
    const behavior = overLimitBehaviorFromLimits(first, second);
    const patch: Partial<ConnectingLineDelayByStationProperties> = {
      over_limit_behavior: behavior,
      max_broadcast_lines: behavior === 'replace_with_phrase' ? second : first,
      replace_over_lines: behavior === 'truncate_then_replace' ? second : undefined,
    };
    if (!usesOverLimitPhrase(behavior)) {
      patch.over_limit_ssml_template = undefined;
      patch.over_limit_ssml_template_i18n = undefined;
    }
    emit(patch);
  };

  /**
   * 1 つめ「何路線まで遅延放送するか」の変更。
   *
   * 併用の 2 つめは 1 つめより大きい値に限るため、選んだ 1 つめが 2 つめ以上になるときは、
   * 食い違いを黙って残さず 2 つめだけ「制限しない」に戻す。
   */
  const changeFirstLimit = (raw: string): void => {
    const nextFirst = raw === '' ? undefined : Number(raw);
    const secondStillValid =
      nextFirst === undefined || secondValue === undefined || secondValue > nextFirst;
    applyLimits(nextFirst, secondStillValid ? secondValue : undefined);
  };

  /** 2 つめ「遅延が何路線を超えたら定型文にするか」の変更。 */
  const changeSecondLimit = (raw: string): void => {
    applyLimits(firstValue, raw === '' ? undefined : Number(raw));
  };

  /** 接続線の件数を超えた保存済みの 1 つめは、選択肢から消えないよう末尾に残す。 */
  const firstSavedBeyondLines =
    firstValue !== undefined && firstValue > lines.length ? firstValue : null;

  /** 2 つめの選択肢の下限。 1 つめが決まっていれば「1 つめ + 1」から、未選択なら 1 から。 */
  const secondMin = firstValue !== undefined ? firstValue + 1 : 1;
  const secondOptions: number[] = Array.from(
    { length: Math.max(0, lines.length - secondMin + 1) },
    (_, i) => secondMin + i,
  );
  /** 範囲外の保存済みの 2 つめ (件数超え・1 つめ以下) は、選択肢から消えないよう残す。 */
  const secondSavedOutOfRange =
    typeof secondValue === 'number' && !secondOptions.includes(secondValue) ? secondValue : null;

  /** 接続線を index から削除する (最後の 1 件は残す) */
  const removeLine = (index: number): void => {
    if (lines.length <= 1) return;
    const nextLines = lines.slice();
    nextLines.splice(index, 1);
    emit({ lines: nextLines });
  };

  /** 接続線を上へ移動 */
  const moveLineUp = (index: number): void => {
    if (index <= 0) return;
    const nextLines = lines.slice();
    const [moved] = nextLines.splice(index, 1);
    if (!moved) return;
    nextLines.splice(index - 1, 0, moved);
    emit({ lines: nextLines });
  };
  /** 接続線を下へ移動 */
  const moveLineDown = (index: number): void => {
    if (index >= lines.length - 1) return;
    const nextLines = lines.slice();
    const [moved] = nextLines.splice(index, 1);
    if (!moved) return;
    nextLines.splice(index + 1, 0, moved);
    emit({ lines: nextLines });
  };

  return (
    <div className="contents-fields">
      {/*
        「乗換駅」と「冒頭 SSML テンプレート」は二段組 (横並び) にして縦スクロールを抑える。

        `.contents-fields` 自体も grid の多段組だが、乗換駅のヒント文 (`<p class="hint">`) は
        CSS ルール `.contents-fields > p { grid-column: 1 / -1 }` で全幅を占有し、
        そこで行が折り返されてしまうため素のままでは縦積みになる。
        そこで 2 項目を `.field-pair-2col` で囲み、ヒント文は左セル (乗換駅) の内側に
        入れることで「必ず横 2 列」を保証する (狭幅では CSS 側で 1 列へフォールバック)。
      */}
      <div className="field-pair-2col" data-testid="connecting-station-header-pair">
        {/* 左セル: 乗換駅 (必須) + ヒント文。 */}
        <div>
          <label>
            <span className="required">乗換駅</span>
            <StopSelect
              ariaLabel="乗換駅"
              testId="connecting-station-transfer-stop-select"
              value={transferStopId}
              stops={parentStops}
              // 未選択で保存した場合に赤枠で強調する (該当欄が一目で分かるように)。
              invalid={hasIssueForTestId(issues, 'connecting-station-transfer-stop-select')}
              onChange={(stopId) => emit({ transfer_stop_id: stopId })}
            />
          </label>
          <p className="hint" style={{ margin: '4px 0 0' }}>
            自路線で選択した路線の駅から、乗換駅を 1 つ選んでください (例:
            「赤羽」)。この駅を主語として、下の接続線ブロックが順に読み上げられます。運行情報だけを放送したい場合は、コンテンツ種別で「運行情報」を選んでください。
          </p>
        </div>

        {/*
          右セル: 冒頭 SSML テンプレート。
        */}
        {!(content?.languages ?? ['ja']).includes('ja') ? null : (
          <label>
            {/*
              Issue #527: 空欄 = 定型文マスタ (`connecting_station_intro`) へ委譲する
              規約へ変更したため **必須ではない**。空欄なら
              マスタ (当該言語 → ja) → コード内既定 の 3 段で解決される。
            */}
            <span>冒頭 SSML テンプレート</span>
            <input
              type="text"
              aria-label="冒頭 SSML テンプレート"
              data-testid="connecting-station-intro-template"
              className={
                hasIssueForTestId(issues, 'connecting-station-intro-template') ? 'is-invalid' : ''
              }
              value={introTemplate}
              /*
                Issue #552: 薄字 (placeholder) は「未入力時に実際に放送される文面」。
                定型文設定 (`connecting_station_intro`) に登録があればその文面、
                無ければコード内既定へフォールバックする
                (放送側 `_render_connecting_station_intro()` と同じ解決順)。
              */
              placeholder={effectiveIntroTemplate}
              onChange={(e) => emit({ ssml_intro_template: e.target.value })}
            />
          </label>
        )}
      </div>
      {/*
        モンキーテスト B-3: 乗換駅名の言語別名称 (`transfer_stop_name_i18n`)。
        従来は乗換駅 (日本語) しか無く、英中韓の放送でも `{transfer_station}` に日本語の
        駅名が入り外国語ボイスが読み上げていた。放送言語に英中韓が含まれるときだけ表示する。
      */}
      <LocalizedRouteNicknameFields
        label="乗換駅名"
        summary="各言語の乗換駅名"
        hint={
          <>
            放送言語ごとに <code>{'{transfer_station}'}</code>{' '}
            へ展開する駅名を設定できます。未入力の言語は日本語の駅名でそのまま案内されます。
          </>
        }
        languages={content?.languages ?? ['ja']}
        value={props.transfer_stop_name_i18n}
        japaneseNickname={transferStopId}
        testIdPrefix="connecting-station-transfer-stop-name"
        onChange={(value) => emit({ transfer_stop_name_i18n: value })}
      />
      {/* 冒頭 SSML の多言語欄 */}
      <LocalizedTextFields
        label="冒頭 SSML テンプレート"
        languages={content?.languages ?? ['ja']}
        value={props.ssml_intro_template_i18n}
        // Issue #552: 各言語の既定文面も定型文マスタ (`connecting_station_intro`) を
        // 反映する (マスタ未登録の言語はコード内既定のまま)。
        defaults={resolveLocalizedDefaults(
          LOCALIZED_STATION_INTRO,
          PHRASE_TEMPLATE_CONNECTING_STATION_INTRO,
          resolveDefault,
        )}
        // Issue #556: 既定文面は **薄字 (placeholder) 専用**。値として入れると
        // 定型文マスタの多言語編集が反映されなくなる (空欄 = マスタ委譲を守る)。
        defaultsAreValues={false}
        // Issue #492 (SBI-11): 翻訳元は同じ欄の日本語テンプレート (冒頭 SSML)。
        //
        // Issue #552: 冒頭文が未入力 (= マスタ委譲) のときは実効文面
        // (マスタ → コード内既定) を翻訳元にする。空文字だと
        // 「日本語から翻訳」ボタンが押せず、多言語の下書きが作れないため。
        sourceText={effectiveIntroTemplate}
        translateTestId="connecting-station-intro"
        onChange={(value) => emit({ ssml_intro_template_i18n: value })}
      />
      {/*
        上部の帯: 左に「通し試聴 + 接続線ヘッダー」、右に「接続線数の上限」を並べて縦スクロールを抑える。
        上限の設定は試聴ボタン行と接続線ヘッダーの右に空きがあるため、そこへ収める。
        狭幅では CSS 側で 1 列に戻す (`.connecting-station-top`)。
        DOM 順は左 (試聴・接続線追加) → 右 (上限) で、Tab 順もこの順になる。
      */}
      <div
        className="contents-fields--full connecting-station-top"
        data-testid="connecting-station-top"
      >
        <div className="connecting-station-top__left">
          {/*
            Issue #439: コンテンツ全体の通し試聴。
            part2 は「冒頭文 + 各接続線ブロック文 + 方面部分文 (読点連結)」の三層構造なので、
            個別の欄だけを聴いても完成形が分からない。サーバ側 (`preview_render.py`) が
            本番と同じ三層の組み立てを行うため、ここで一括プレビューすると
            「文と文のつながりが不自然」といった個別プレビューでは気付けない問題を発見できる。

            プレビュー条件パネルの入力欄は **コンテンツ全体の凡例** から決める
            (`resolveContentPlaceholders`)。冒頭文の凡例 (乗換駅のみ) だけに絞ると、
            三層すべてを鳴らす「通し試聴」なのに遅延分数・線名・方面を指定できず、
            「境界値でどのパターンが鳴るか」を通しで確認できないため。
            サンプル値も同様にコンテンツ設定から自動導出する
            (乗換駅 / 先頭接続線の線名 / 先頭方面 / 遅延パターンの遅れ分数)。
          */}
          {content ? (
            <AudioPreviewField
              content={content}
              parentVoiceId={parentVoiceId}
              playbackRate={playbackRate}
              onPreviewSsml={onPreviewSsml}
              testIdSuffix={`connecting-station-${contentIndex}`}
              label="▶ このコンテンツを通しで試聴"
              placeholders={resolveContentPlaceholders(content)}
              sampleOverrides={deriveContentSampleOverrides(content)}
              disabled={lines.length === 0}
              disabledReason="接続線を追加すると試聴できます"
            />
          ) : null}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
            <strong>接続線 ({lines.length} 件)</strong>
            <button
              type="button"
              data-testid="connecting-station-add-line-button"
              aria-label="接続線を追加"
              onClick={addLineToEnd}
            >
              ＋ 接続線を追加
            </button>
          </div>
        </div>
        {/*
        遅延放送する路線数の制限。数えるのは「遅延案内文が空でない接続線」だけで、
        運行情報のみの接続線 (運転見合わせを含む) は件数に入らない (放送側の仕様)。
        プルダウン 2 つを常に操作できる状態で表示する。先頭は「制限しない」で、両方を制限しないにすれば
        上限なし。動作 (`over_limit_behavior`) は 2 つの組み合わせから決める。
        操作前に読めばよい説明は ⓘ に集約し、警告を表す文は隐さずに残す。
      */}
        <fieldset
          className="connecting-station-top__limit"
          data-testid="connecting-station-over-limit"
          style={{ margin: 0, minWidth: 0 }}
        >
          <legend>遅延放送の路線数</legend>
          <div>
            <span>
              {FIRST_LIMIT_LABEL}
              <HintTooltip
                label={`${FIRST_LIMIT_LABEL}の説明`}
                testId="connecting-station-max-broadcast-lines-hint"
              >
                遅延案内文のある接続線がこの路線数を超えると、先頭からこの路線数まで放送し、残りは放送しません。
                「制限しない」ならすべて放送します。運行情報のみの接続線や運転見合わせは数えません。
              </HintTooltip>
            </span>{' '}
            <select
              aria-label={FIRST_LIMIT_LABEL}
              data-testid="connecting-station-max-broadcast-lines"
              className={
                hasIssueForTestId(issues, 'connecting-station-max-broadcast-lines')
                  ? 'is-invalid'
                  : ''
              }
              value={firstValue !== undefined ? String(firstValue) : ''}
              onChange={(e) => changeFirstLimit(e.target.value)}
            >
              <option value="">制限しない</option>
              {Array.from({ length: lines.length }, (_, i) => i + 1).map((n) => (
                <option key={n} value={String(n)}>
                  {n} 路線
                </option>
              ))}
              {firstSavedBeyondLines !== null ? (
                <option value={String(firstSavedBeyondLines)}>
                  {firstSavedBeyondLines} 路線 (接続線の件数を超えています)
                </option>
              ) : null}
            </select>
          </div>
          <div style={{ marginTop: 8 }}>
            <span>
              {SECOND_LIMIT_LABEL}
              <HintTooltip
                label={`${SECOND_LIMIT_LABEL}の説明`}
                testId="connecting-station-replace-over-lines-hint"
              >
                遅延案内文のある接続線がこの路線数を超えると、各路線の案内の代わりに、固定の案内文
                (定型文) を 1 本だけ放送します。「{FIRST_LIMIT_LABEL}
                」と併用するときは、それより大きい路線数を選びます。
              </HintTooltip>
            </span>{' '}
            <select
              aria-label={SECOND_LIMIT_LABEL}
              data-testid="connecting-station-replace-over-lines"
              className={
                hasIssueForTestId(issues, 'connecting-station-replace-over-lines')
                  ? 'is-invalid'
                  : ''
              }
              value={typeof secondValue === 'number' ? String(secondValue) : ''}
              onChange={(e) => changeSecondLimit(e.target.value)}
            >
              <option value="">制限しない</option>
              {secondOptions.map((n) => (
                <option key={n} value={String(n)}>
                  {n} 路線
                </option>
              ))}
              {secondSavedOutOfRange !== null ? (
                <option value={String(secondSavedOutOfRange)}>
                  {secondSavedOutOfRange} 路線{' '}
                  {secondSavedOutOfRange > lines.length
                    ? '(接続線の件数を超えています)'
                    : '(範囲外です)'}
                </option>
              ) : null}
            </select>
          </div>
          {overLimitBehavior !== undefined && isOverLimitNotEffective(props) ? (
            <StatusBanner variant="warning" data-testid="connecting-station-over-limit-warning">
              <strong>路線数が接続線の件数以上のため、制限は効きません。</strong>
              路線数を超えることがないので、制限しない場合と同じ放送になります（このままでも保存できます）。
            </StatusBanner>
          ) : null}
          {isReplaceOverLinesNotEffective(props) ? (
            <StatusBanner
              variant="warning"
              data-testid="connecting-station-replace-over-lines-warning"
            >
              <strong>
                定型文にする路線数が接続線の件数以上のため、定型文には切り替わりません。
              </strong>
              遅延のある接続線が件数を超えることはないので、打ち切りのみの放送と同じになります（このままでも保存できます）。
            </StatusBanner>
          ) : null}
          {/*
          上限超過時の案内文。固定文を使う動作 (固定文のみ / 併用) のときだけ入力できる。
          打ち切りのみでは固定文を使わないため、言語別の欄も含めて出さない
          (LocalizedTextFields に disabled が無く、共有部品を変えないための割り切り)。
          空欄は定型文マスタ `connecting_station_over_limit` へ委譲する (薄字 = 実際に放送される文面)。
        */}
          {!(content?.languages ?? ['ja']).includes('ja') ? null : (
            <label style={{ display: 'block', marginTop: 8 }}>
              <span>上限超過時の案内文</span>
              <textarea
                rows={2}
                aria-label="上限超過時の案内文"
                data-testid="connecting-station-over-limit-template"
                className={
                  hasIssueForTestId(issues, 'connecting-station-over-limit-template')
                    ? 'is-invalid'
                    : ''
                }
                style={{ width: '100%', boxSizing: 'border-box' }}
                disabled={!isReplaceWithPhrase}
                value={overLimitTemplate}
                placeholder={effectiveOverLimitTemplate}
                onChange={(e) => emit({ over_limit_ssml_template: e.target.value })}
              />
            </label>
          )}
          {isReplaceWithPhrase ? (
            <LocalizedTextFields
              label="上限超過時の案内文"
              languages={content?.languages ?? ['ja']}
              value={props.over_limit_ssml_template_i18n}
              defaults={resolveLocalizedDefaults(
                LOCALIZED_STATION_OVER_LIMIT,
                PHRASE_TEMPLATE_CONNECTING_STATION_OVER_LIMIT,
                resolveDefault,
              )}
              // 既定文面は薄字専用。値にするとマスタの多言語編集が反映されなくなる (Issue #556)。
              defaultsAreValues={false}
              sourceText={effectiveOverLimitTemplate}
              translateTestId="connecting-station-over-limit"
              issues={issues}
              onChange={(value) => emit({ over_limit_ssml_template_i18n: value })}
            />
          ) : null}
        </fieldset>
      </div>

      {/* 接続線ブロック一覧 */}
      <div style={{ marginTop: 4 }}>
        {lines.length === 0 ? (
          <div className="empty-state" style={{ padding: '8px' }}>
            接続線が設定されていません。「＋ 接続線を追加」から作成してください。
          </div>
        ) : null}

        {/*
         * 接続線ブロックはタブ表示で横並びにする。各タブは接続線の路線カラーで着色し、
         * 選択中の 1 ブロックだけ本文 (ConnectingLineBlock) を表示する。
         */}
        {lines.length > 0 ? (
          <>
            <div className="connecting-line-tabs" role="tablist" data-testid="connecting-line-tabs">
              {lines.map((line, i) => {
                const color = getRouteColor(routes, line.route_id ?? '');
                const routeName =
                  routes.find((r) => r.route_id === line.route_id)?.route_name ||
                  line.route_id ||
                  `接続線#${i + 1}`;
                const isActive = i === activeIndex;
                return (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    data-testid={`connecting-line-tab-${i}`}
                    className={`connecting-line-tab${isActive ? ' is-active' : ''}`}
                    style={{
                      // 路線カラーを常に左端のバーで表示し、選択中はタブ全体を路線カラーで塗る。
                      background: isActive ? color.background : '#fff',
                      color: isActive ? color.text : 'var(--color-fg)',
                      borderBottomColor: color.background,
                    }}
                    onClick={() => setActiveLineIndex(i)}
                  >
                    <span
                      className="connecting-line-tab__swatch"
                      style={{ background: color.background }}
                      aria-hidden="true"
                    />
                    #{i + 1} {routeName}
                    {/*
                      この接続線にエラーがあることをタブ上で示す。
                      タブを開かないとエラーの有無すら分からない状態を避けるための表示で、
                      クリックすればそのままタブが切り替わって該当欄を確認できる。
                    */}
                    {lineHasIssue(i) ? (
                      <span
                        className="connecting-line-tab__error"
                        data-testid={`connecting-line-tab-error-${i}`}
                        aria-label={`接続線#${i + 1} に入力エラーあり`}
                      >
                        {' '}
                        ⚠
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            <ol className="connecting-station-line-list" style={{ listStyle: 'none', padding: 0 }}>
              {lines.map((line, i) =>
                i === activeIndex ? (
                  <li
                    key={i}
                    className="connecting-station-line-item"
                    data-testid={`connecting-station-line-${i}`}
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      padding: '8px 12px',
                      marginTop: 8,
                    }}
                  >
                    <ConnectingLineBlock
                      index={i}
                      line={line}
                      routes={routes}
                      routeStops={routeStops}
                      istationsLines={istationsLines}
                      // Issue #439: 接続線ブロック内 (方面部分文 SSML の行ごと) の
                      // 音声プレビューに必要な情報を渡す。
                      content={content}
                      contentIndex={contentIndex}
                      parentVoiceId={parentVoiceId}
                      playbackRate={playbackRate}
                      onPreviewSsml={onPreviewSsml}
                      canRemove={lines.length > 1}
                      isFirst={i === 0}
                      isLast={i === lines.length - 1}
                      // この接続線に絞ったエラーを渡す (route_id / 判定モード / 乗換時間 /
                      // ブロック SSML / 遅延パターン行の赤枠と、方面タブの ⚠ に使う)。
                      issues={issues.filter((issue) => issue.target.lineIndex === i)}
                      // 方面タブを自動で開くための指示 (さらに下位へ中継される)。
                      revealTarget={revealTarget}
                      onChange={(next) => updateLine(i, next)}
                      // 追加した接続線を選択中タブにする処理は addLineAfter 内で行う
                      // (ヘッダーの「＋ 接続線を追加」と挙動を揃えるため)。
                      onAdd={() => addLineAfter(i)}
                      // 複製した接続線を選択中タブにする処理は
                      // duplicateLineAfter 内で行う (＋追加と挙動を揃えるため)。
                      onDuplicate={() => duplicateLineAfter(i)}
                      onRemove={() => {
                        removeLine(i);
                        setActiveLineIndex(Math.max(0, i - 1));
                      }}
                      onMoveUp={() => {
                        moveLineUp(i);
                        setActiveLineIndex(Math.max(0, i - 1));
                      }}
                      onMoveDown={() => {
                        moveLineDown(i);
                        setActiveLineIndex(Math.min(lines.length - 1, i + 1));
                      }}
                    />
                  </li>
                ) : null,
              )}
            </ol>
          </>
        ) : null}
      </div>
    </div>
  );
}
